/* istanbul ignore file */

import type { Href, UnknownInputParams } from 'expo-router'
import { Platform } from 'react-native'

import type { TPublicationPageSearchParams } from '@/src/app/publications/[id]'
import type { TPublicationRewardPraySearchParams } from '@/src/app/publications/[id]/comment/[commentId]/pray'
import type { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import type { TGetIntentionsPayload } from '@/src/features/intention/api/types/getIntentionsApi.types'
import { INTENTION_TYPE } from '@/src/features/intention/models/Intention.enums'
import type { GENERAL_NOTIFICATION_SETTINGS } from '@/src/features/notification/models/notification.constants'
import type { TWatchmanSearchParams } from '@/src/features/watchman/ui/screens/WatchmanScreen'
import { getLegacyUrl } from '@/src/general/utils/getLegacyUrl'
import type { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import type { TT } from '@/src/lang/useTranslate'

import { getSearchParams } from '../utils/getSearchParams'
import type { AUTH_SOURCE } from '../utils/gtm/constants/AuthSources.enum'

export const getRoute = <T extends string>(pathname: T, params?: UnknownInputParams) => ({
  pathname,
  params,
})

const getUrlSearchParams = (params: UnknownInputParams | undefined) => {
  // oxlint-disable-next-line hozana-local/boolean-variable-naming
  const paramsArray =
    params &&
    Object.values(params).length > 0 &&
    Object.entries(params).reduce<string[][]>((acc, [key, values]) => {
      if (values === undefined || values === null) return acc
      if (Array.isArray(values)) {
        values.forEach((value) => acc.push([key, value.toString()]))
        return acc
      }
      acc.push([key, values.toString()])
      return acc
    }, [])
  return paramsArray ? `?${new URLSearchParams(paramsArray)}` : ''
}

export const routeToPath = (route: Href): string => {
  if (typeof route === 'string') return route
  const { pathname, params } = route
  const { search } = getSearchParams(params)
  return pathname + search
}

export type TSourceParam = { source: TRACKING_SOURCE }
export type TEventOriginParam = { origin: EVENT_ORIGIN }

export type TCommunityJoinParams = TSourceParam &
  TEventOriginParam & {
    /** How did the user join the community, directly using a 'join' button somewhere, or visiting the community page first ? */
    course: 'direct' | 'page' | 'undefined'
    pik?: string
  }
type TPublicationCommentPray = { publicationId: number; commentId: number } & TPublicationRewardPraySearchParams
type TIdShareToken = { id: number; shareToken?: string }
export type TIntentionsPraySearchParams = TSourceParam & { theme: string | undefined }

type TIntentionsPrayParams = { type: INTENTION_TYPE } & TIdShareToken & TIntentionsPraySearchParams

type TAuthParams = { redirectTo: string; authSource?: AUTH_SOURCE; email?: string }

export const makeRoutes = (lang: string, t: TT) => ({
  askWatchman: (params: TWatchmanSearchParams) => getRoute('/watchman', params),
  askWatchmanReward: (params?: { action?: 'subscribe-watchman'; theme?: string }) =>
    getRoute('/watchman/reward', params),
  authName: (params: { redirectTo: string }) => getRoute('/auth/name', params),
  authOtp: (params: { redirectTo: string }) => getRoute('/auth/otp', params),
  connect: (redirectTo: string) =>
    `${getLegacyUrl(t('url.connect'), lang)}?redirectToExternal=${encodeURIComponent(redirectTo)}`,
  authConnect: (params?: TAuthParams) => getRoute('/auth/email', params),
  authComplete: (params: TAuthParams & { emailHash?: string }) => getRoute('/auth/complete', params),
  connectWithEmail: ({ email, redirectTo }: { email: string; redirectTo: string }) =>
    `${getLegacyUrl(t('url.connect'), lang)}?email=${encodeURIComponent(email)}&redirectToExternal=${encodeURIComponent(redirectTo)}`,
  facebookConfirm: () => '/auth/facebook/confirm',
  socialAuth: () => '/auth/social/callback',
  publication: ({ id, slug, ...params }: { id: number; slug?: string } & TSourceParam) =>
    getRoute(`/publications/${id}` + (slug ? `-${slug}` : ''), params),
  publicationById: ({ id, ...params }: { id: number } & TPublicationPageSearchParams) =>
    getRoute(`/publications/${id}`, params),
  publicationByIdReward: (id: number, params: TSourceParam) => getRoute(`/publications/${id}/reward`, params),
  publicationByIdRewardPray: (id: string, intentionId: string, shareToken: string) =>
    `/publications/${id}/reward/${intentionId}-${shareToken}/pray`,
  publicationCommentPray: ({ publicationId, commentId, ...params }: TPublicationCommentPray) =>
    getRoute(`/publications/${publicationId}/comment/${commentId}/pray`, params),
  intentionCreationPray: ({ id, shareToken }: TIdShareToken & TSourceParam) =>
    `/intention/create/${id}-${shareToken}/pray`,
  intentionById: (id: number, shareToken: string) => `/intention/${id}-${shareToken}`,
  intentionByIdReward: ({
    id,
    shareToken,
    type,
    ...params
  }: TIntentionsPrayParams & { redirect: string; prayerId: number | null }) =>
    getRoute(
      type === INTENTION_TYPE.INTENTION
        ? `/intention/${id}-${shareToken}/reward`
        : `/intention/${id}-${shareToken}/${type}/reward`,
      params,
    ),
  intentionByIdPrayers: (id: number, type: INTENTION_TYPE) =>
    getRoute('/intention/[id]/prayers', type === INTENTION_TYPE.INTENTION ? { id } : { type, id }),
  intentions: (theme?: string) => (theme ? `/intentions/${theme}` : '/intentions'),
  intentionsForm: (theme?: string) => `/intention/create${theme ? `?theme=${theme}` : ''}`,
  intentionCreateReward: () => '/intention/create/reward',
  intentionsPray: ({ id, shareToken, type, ...params }: TIntentionsPrayParams) =>
    getRoute(
      `/intention/${id}${shareToken ? `-${shareToken}` : ''}${type !== INTENTION_TYPE.INTENTION ? `/${type}` : ''}/pray`,
      params,
    ),
  // Hide feature for now
  messages: () => '/messages',
  newConversationById: (userId: number) => `/messages?userId=${userId}`,
  conversationById: (conversationId: number) => `/messages/${conversationId}`,
  conversationUserPray: (userId: number, conversationId: number | string) =>
    `/messages/${conversationId}/${userId}/pray`,
  conversationReport(conversationId: number) {
    return `${this.conversationById(conversationId)}/report`
  },
  newConversation: (userId: number) => getRoute('/messages', { userId }),
  donation: () => getLegacyUrl(t('url.donate'), lang),
  reportIntentionAbuse: (idSharetoken: TIdDashSlug, intentionsQueryPayload: TGetIntentionsPayload) =>
    getRoute(`/intention/${idSharetoken}/report-abuse`, {
      intentionsQueryPayload: encodeURI(JSON.stringify(intentionsQueryPayload)),
    }),
  // Tabs
  'prayer-space': () => `/prayer-space`,
  communities: (params?: { message?: string; action?: string }) => getRoute(`/communities`, params),

  community: ({ id, slug, ...params }: TIdSlug & TEventOriginParam & { redirect?: string; message?: string }) =>
    getRoute(`/community/${id}-${slug}`, params),
  communityJoin: ({ id, slug, ...params }: { id: number; slug?: string } & Partial<TCommunityJoinParams>) =>
    getRoute(`/community/${id}${slug ? '-' + slug : ''}/join`, params),
  communityJoinDate: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`/community/${id}-${slug}/join/date`, params),
  communityJoinNotifications: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`/community/${id}-${slug}/join/notifications`, params),
  communityJoinMail: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`/community/${id}-${slug}/join/mails`, params),
  communityJoinCongrats: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`/community/${id}-${slug}/join/congrats`, params),
  communityJoinNext: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`/community/${id}-${slug}/join/next`, params),
  communityJoinNextIntentionPray: ({
    id,
    slug,
    intentionId,
    shareToken,
    ...params
  }: TIdSlug & { intentionId: number; shareToken: string } & TSourceParam) =>
    getRoute(`/community/${id}-${slug}/join/next/${intentionId}-${shareToken}/pray`, params),
  communityLegacy: ({ id, slug, ...params }: TIdSlug & { redirect?: string; action?: string; message?: string }) =>
    getLegacyUrl(`${t('url.community', { id, slug })}${getUrlSearchParams(params)}`, lang),
  communityLanding: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getLegacyUrl(`${t('url.community', { id, slug })}/landing${getUrlSearchParams(params)}`, lang),
  communityLandingPath: ({ id, slug, ...params }: TIdSlug & TCommunityJoinParams) =>
    getRoute(`${t('url.community', { id, slug })}/landing`, params),
  communityAdmin: ({ id, slug: _slug, ...params }: TIdSlug & { tab?: string }) =>
    getLegacyUrl(`/admin/community${getUrlSearchParams({ ...params, communityId: id })}`, lang),
  preferences: () => '/preferences',
  animatorSpace: () => getLegacyUrl('/admin/animator-space', lang),
  parameters: () => '/settings/account',
  events: () => getLegacyUrl(t('url.menu.events'), lang),
  page: (slug: string) => `/page/${slug}`,
  faq() {
    return this.page('faq')
  },
  contact: () => getLegacyUrl(t('url.menu.contact'), lang),
  introduction: () => getLegacyUrl(t('url.menu.introduction'), lang),
  about: () => getLegacyUrl(t('url.menu.about'), lang),
  communitiesList: () => getLegacyUrl(t('url.menu.communities-list'), lang),
  communitiesCategory: (tag: string) => '/communities/' + tag,
  communitiesSearch: () => '/communities/search',
  communitiesResearch: (search: string) => getRoute('/communities', { search }),
  favorites: () => '/favorites',
  guides: () => getLegacyUrl(t('url.menu.guides-list'), lang),
  subscriptionsHistory: () => '/subscriptions/history',
  cgu: () => getLegacyUrl(t('url.menu.cgu'), lang),
  editPublication: (id: number) => getLegacyUrl(`/admin/publication/edit?publicationId=${id}`, lang),
  communityNotifications: ({ id, slug, updateParametersToken }: TIdSlug & { updateParametersToken?: string }) =>
    getRoute(`/community/${id}-${slug}/notifications`, updateParametersToken ? { updateParametersToken } : undefined),
  leaveCommunity: (id: string) => getRoute(`/community/${id}`, { action: 'leave' }),
  rosarioRoses: (params?: { redirect: string }) => getRoute('/rosario/roses', params),
  settings: () => '/settings',
  settingsAccount: () => '/settings/account',
  settingsAvatars: () => '/settings/account/avatars',
  settingsAvatar: (id: number) => `/settings/account/avatars/${id}`,
  settingsGeneral: (params?: { updateParametersToken?: string; setting?: GENERAL_NOTIFICATION_SETTINGS }) =>
    getRoute('/settings/notifications/general', params),
  settingsCommunities: (params?: { updateParametersToken?: string }) =>
    getRoute('/settings/notifications/communities', params),
  uploadAvatar: () => getLegacyUrl('/admin/upload-avatar'),
  communityCreation: () => getLegacyUrl('/admin/community/create', lang),
  home: () => Platform.select({ web: getLegacyUrl('/', lang), default: '/' }),
  app: () => getLegacyUrl('app?utm_medium=app_menu_link'),
  leaveHozana: () => '/leave',
  watchmanIntentions: () => '/watchman/intentions',
  watchmanIntentionsPray: () => '/watchman/intentions/pray',
  watchmanIntentionsReward: () => '/watchman/intentions/reward',
  watchmanPushCta: () => '/watchman/intentions/notifications',
})
